import { useLabel } from "@/core/context/LabelsContext"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import {
  DiscoDropdown,
  DiscoIcon,
  DiscoIconButton,
  DiscoSwitch,
  DiscoText,
  DiscoTooltip,
} from "@disco-ui"
import DiscoDropdownItem from "@disco-ui/dropdown/DiscoDropdownItem"
import { useTheme } from "@material-ui/core"
import useFeatureFlags from "@utils/hook/useFeatureFlags"
import { TestIDProps } from "@utils/typeUtils"
import { observer } from "mobx-react-lite"
import { useCallback } from "react"
import { GenerateWithAIFormStore } from "./GenerateWithAIForm"

interface Props extends TestIDProps {
  form: GenerateWithAIFormStore
  mobileCount: number
}

function GenerateWithAIFormToolDropdown({
  testid = "GenerateWithAIFormToolDropdown",
  mobileCount,
  form,
}: Props) {
  const { aiSources } = useFeatureFlags()
  const classes = useStyles()
  const theme = useTheme()
  const organizationLabel = useLabel("organization")

  return (
    <DiscoDropdown
      menuClasses={{ paper: classes.aiToolDropdown }}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      disableCloseOnClick
      disableScrollLock
      menuButton={useCallback(
        ({ onClick }) => (
          <DiscoTooltip content={"Other tools"}>
            <DiscoIconButton
              height={32}
              width={32}
              onClick={onClick}
              testid={`${testid}.ai-tool-settings`}
              className={`${classes.controlButton} ${classes.webSearchButton}`}
              svgStyles={{
                width: 20,
                height: 20,
              }}
            >
              <DiscoIcon icon={"setting-tools"} />
            </DiscoIconButton>
          </DiscoTooltip>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
          classes.controlButton,
          classes.webSearchButton,
          theme.palette.aiColors.blue,
          mobileCount,
        ]
      )}
    >
      <DiscoDropdownItem
        className={classes.item}
        hoverBackgroundColor={"transparent"}
        color={theme.palette.groovy.neutral[500]}
        hoverColor={theme.palette.groovy.neutral[500]}
        title={
          <span className={classes.tooltipTitle}>
            <DiscoText>{"Search the web"}</DiscoText>
            <DiscoTooltip
              content={
                "Enable web search to include relevant online resources in the generation."
              }
            />
          </span>
        }
        iconSize={16}
        icon={"globe"}
        rightButton={
          <DiscoSwitch
            checked={Boolean(form.state.searchWeb)}
            onChange={(val) => (form.state.searchWeb = val)}
          />
        }
      />

      {aiSources && (
        <DiscoDropdownItem
          className={classes.item}
          hoverBackgroundColor={"transparent"}
          color={theme.palette.groovy.neutral[500]}
          hoverColor={theme.palette.groovy.neutral[500]}
          title={
            <span className={classes.tooltipTitle}>
              <DiscoText
                variant={"body-sm"}
              >{`Use ${organizationLabel.singularLowercase} context`}</DiscoText>
              <DiscoTooltip
                content={`Use all content from ${organizationLabel.singularLowercase} for the generation.`}
              />
            </span>
          }
          iconSize={16}
          icon={"grid-thick"}
          rightButton={
            <DiscoSwitch
              checked={Boolean(form.state.communityContext)}
              onChange={(val) => (form.state.communityContext = val)}
            />
          }
        />
      )}
    </DiscoDropdown>
  )
}

const useStyles = makeUseStyles((theme) => ({
  aiToolDropdown: {
    width: "320px",
  },
  controlButton: {
    background: "transparent",
    "&:hover": {
      background: theme.palette.action.hover,
    },
  },
  webSearchButton: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  tooltipTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  item: {
    cursor: "default",
  },
}))

export default observer(GenerateWithAIFormToolDropdown)
